import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Loader2, MailCheck } from "lucide-react";
import { requestPasswordReset } from "@/features/auth/lib/forgotPassword";
import {
  AdgeLogo,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@nexus/ui";

type Step = "form" | "sent";

export function ForgotPasswordForm() {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>("form");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email || loading) return;
    setLoading(true);
    try {
      await requestPasswordReset(email);
    } catch {
      // requestPasswordReset swallows errors, but guard here too
    }
    setLoading(false);
    setStep("sent");
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--background)] p-4">
      <Card className="w-full max-w-sm">
        {step === "form" ? (
          <>
            <CardHeader className="space-y-1 text-start">
              <AdgeLogo className="mb-2 origin-start" aria-label={t("auth.brandName")} />
              <CardTitle className="text-xl font-normal">{t("auth.forgotPasswordTitle")}</CardTitle>
              <CardDescription className="text-sm">{t("auth.forgotPasswordDescription")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="space-y-2">
                  <Label htmlFor="reset-email">{t("auth.email")}</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    autoFocus
                    disabled={loading}
                  />
                </div>
                <Button type="submit" className="w-full gap-2" disabled={loading || !email} aria-busy={loading}>
                  {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  <span>{loading ? t("auth.forgotPasswordSubmitting") : t("auth.forgotPasswordSubmit")}</span>
                </Button>
              </form>
              <div className="text-center text-sm">
                <Link to="/login" className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] underline-offset-4 hover:underline">
                  {t("auth.forgotPasswordBackToLogin")}
                </Link>
              </div>
            </CardContent>
          </>
        ) : (
          <>
            <CardHeader className="space-y-1 text-start">
              <AdgeLogo className="mb-2 origin-start" aria-label={t("auth.brandName")} />
              <div className="flex items-center gap-2">
                <MailCheck className="h-5 w-5 text-[var(--muted-foreground)]" aria-hidden="true" />
                <CardTitle className="text-xl font-normal">{t("auth.forgotPasswordSentTitle")}</CardTitle>
              </div>
              <CardDescription className="text-sm">{t("auth.forgotPasswordSentDescription")}</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/login">
                <Button variant="outline" className="w-full">
                  {t("auth.forgotPasswordBackToLogin")}
                </Button>
              </Link>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}
